import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'

import agent from 'agent'
import PageSection from 'components/common/PageSection'
import { PromptDialog } from 'components/common/PromptDialog'
import { CreateTestSuiteForm } from 'components/test-suites/forms/CreateTestSuiteForm'
import { EditTestSuiteForm } from 'components/test-suites/forms/EditTestSuiteForm'
import {
  generateTestSuitePatchRequest,
  generateTestSuitePostRequest,
  getTestSuiteFormInitialValues,
  getTestSuiteValidationSchema,
  TestSuiteFormValues
} from 'components/test-suites/forms/test-suite-form-utils'
import {
  TestSuiteFormMode,
  useTestSuiteFormContext
} from 'components/test-suites/forms/TestSuiteFormContext'
import { useGetTestSuitesUrls } from 'components/test-suites/hooks/use-get-test-suites-urls'
import useAuth from 'hooks/use-auth'
import { serviceQueryKeys } from 'hooks/use-services'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'

const WarningSaveDialog: React.FC<{
  isOpen: boolean
  isSaving: boolean
  handleConfirmClick: () => void
  onClose: () => void
}> = ({ isOpen, isSaving, handleConfirmClick, onClose }) => {
  const { trans } = useTranslation()

  return (
    <Dialog
      aria-labelledby="alert-dialog-title"
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle id="alert-dialog-title">
        {trans('test-suites.save_dialog.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {trans('test-suites.save_dialog.content')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{trans('cancel')}</Button>
        <LoadingButton
          autoFocus
          data-testid="test-suite-save-warning-button"
          loading={isSaving}
          onClick={handleConfirmClick}
        >
          {trans('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export const TestSuiteForm: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const testSuitesUrls = useGetTestSuitesUrls()
  const { language, trans } = useTranslation()
  const { currentTeamId } = useAuth()

  const [isSaveModalOpen, setIsSaveModalOpen] = useBoolean(false)
  const [isSaving, setIsSaving] = useBoolean(false)
  const [isRequestMadeSuccessfully, setIsRequestMadeSuccessfully] =
    useBoolean(false)

  const {
    isErrorScenarios,
    isLoadingScenarios,
    isOfficialTestSuite,
    queryResultTestSuite,
    testSuiteClone,
    testSuiteToEdit,
    testSuiteFormMode
  } = useTestSuiteFormContext()

  const isEdit = testSuiteFormMode === TestSuiteFormMode.Edit

  const testSuite = isEdit ? testSuiteToEdit : testSuiteClone

  const testSuiteValidationSchema = getTestSuiteValidationSchema()

  const defaultValues = useMemo(() => {
    return getTestSuiteFormInitialValues(testSuite, language)
  }, [testSuite, language])

  const form = useForm<TestSuiteFormValues>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(testSuiteValidationSchema)
  })

  const { formState, getValues, handleSubmit, reset, watch } = form

  const [targetTeamIds] = watch(['targetTeamIds'])

  /**
   * In most cases, a change to a test suite will impact running RM tests.
   * We need to display the warning modal:
   * - if editing a test suite and teams _were_ and/or _are_ assigned to the suite
   * - if creating a test suite and teams are assigned to the suite
   *
   * Note: there are more cases where we don't need to display a warning, but
   * calculating all the cases here isn't worth it right now
   * (especially since a test suite manager can't check if teams they aren't a part of have tests running or not.)
   */
  const shouldShowWarning = isEdit
    ? defaultValues.targetTeamIds.length > 0 || targetTeamIds.length > 0
    : targetTeamIds.length > 0

  /**
   * POST - save new test suite
   * PATCH - update test suite
   */
  const makePostOrPatchRequest = async (formValues: TestSuiteFormValues) => {
    setIsSaving(true)
    try {
      if (isEdit) {
        const patchRequest = generateTestSuitePatchRequest(
          formValues,
          isOfficialTestSuite
        )
        await agent.patch(
          `/test-suites/${testSuite?.identifier}?teamId=${currentTeamId}`,
          patchRequest
        )

        await queryResultTestSuite.refetch()
      } else {
        await agent.post(
          `/test-suites?teamId=${currentTeamId}`,
          generateTestSuitePostRequest(formValues)
        )
      }
      setIsSaving(false)
      setIsRequestMadeSuccessfully(true)

      if (isSaveModalOpen) {
        setIsSaveModalOpen(false)
      }

      /**
       * Refresh services only if current team is affected
       */
      const shouldInvalidate = Boolean(
        defaultValues.targetTeamIds
          .concat(formValues.targetTeamIds)
          .find((teamId) => teamId === currentTeamId)
      )

      if (shouldInvalidate) {
        await queryClient.invalidateQueries(serviceQueryKeys.allPaged)
      }

      reset()

      toast({
        type: 'success',
        text: isEdit
          ? trans('test_suite.edit.update_success')
          : trans('test_suite.new.create_success'),
        time: 2_000
      })
    } catch (err) {
      const error = messageFromResponseError(
        err,
        isEdit
          ? trans('test_suite.edit.update_error')
          : trans('test_suite.new.create_error')
      )
      setIsSaving(false)

      toast({ type: 'error', text: error })
    }
  }

  const onSubmit = async (formValues: TestSuiteFormValues) => {
    if (shouldShowWarning) {
      setIsSaveModalOpen(true)
      return
    }

    await makePostOrPatchRequest(formValues)
  }

  useEffect(() => {
    if (
      !isEdit &&
      isRequestMadeSuccessfully &&
      !formState.isDirty &&
      !formState.isSubmitting
    ) {
      navigate(testSuitesUrls.base())
    }
  }, [
    formState.isDirty,
    formState.isSubmitting,
    isEdit,
    isRequestMadeSuccessfully,
    navigate,
    testSuitesUrls
  ])

  return (
    <PageSection
      errorMessage={
        <Typography>
          {trans('test_suites.errors.page_loading_error')}
        </Typography>
      }
      hasError={isErrorScenarios}
      isLoading={isLoadingScenarios}
    >
      <PromptDialog formState={formState} reset={reset} />
      <WarningSaveDialog
        handleConfirmClick={async () =>
          await makePostOrPatchRequest(getValues())
        }
        isOpen={isSaveModalOpen}
        isSaving={isSaving}
        onClose={() => setIsSaveModalOpen(false)}
      />
      <FormProvider {...form}>
        <form
          data-testid="create-test-suite-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isEdit && testSuiteToEdit ? (
            <EditTestSuiteForm shouldShowWarning={shouldShowWarning} />
          ) : (
            <CreateTestSuiteForm
              isSaving={isSaving}
              shouldShowWarning={shouldShowWarning}
              onHandleNextWithWarning={() => setIsSaveModalOpen(true)}
            />
          )}
        </form>
      </FormProvider>
    </PageSection>
  )
}
